'use client';

import Image from '@/components/Image';
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useInViewportAnimation } from '@/hooks/useInViewportAnimation';
import { EPlatform } from '@/utils/enums/platform-enum';

// 优势项接口定义
interface AdvantageItem {
  image: string;
  title: string;
}

// Advantages 组件接口定义
interface BlueLightAdvantagesProps {
  advantagesData: {
    title?: string;
    left_image?: string;
    words?: AdvantageItem[];
  };
  deviceType?: EPlatform;
  isFromCache?: boolean;
  onSectionExp?: (params: any) => void;
}

export default function BlueLightAdvantages({
  advantagesData,
  deviceType,
  isFromCache = false,
  onSectionExp,
}: BlueLightAdvantagesProps) {
  const isMobile = deviceType !== EPlatform.WEB;

  // 使用 useInView 进行模块曝光检测
  const [expRef, inView] = useInView({
    threshold: 0.5, // 50%可见时触发
    triggerOnce: true, // 只触发一次
  });

  // isFromCache 现在通过参数传入，无需检测

  // 整体区域动画 - 只在PC端执行
  const advantagesRef = useInViewportAnimation(
    () => {
      if (isFromCache || isMobile) return;

      try {
        // 图片动画
        const img = document.getElementsByClassName('advantages-img-wrap-img');
        if (img[0]) {
          img[0].classList.add('activityFadeInLeftAnimate');
        }

        // 标题动画
        const title = document.getElementsByClassName('advantages-right-part-h2');
        if (title[0]) {
          title[0].classList.add('activityFadeInUpAnimate');
        }

        // 列表项动画
        const listItems = document.getElementsByClassName('advantages-right-part-li');
        Array.from(listItems).forEach((item, index) => {
          setTimeout(() => {
            item.classList.add('activityFadeInUpAnimate');
          }, index * 200); // 错开动画时间
        });
      } catch {
        // 出错时直接显示
        const elements = document.querySelectorAll('.blue-light-advantages-part .opacity0');
        elements.forEach((el) => {
          (el as HTMLElement).style.opacity = '1';
        });
      }
    },
    { threshold: 0.3, once: true },
  );

  // 模块曝光埋点处理
  useEffect(() => {
    if (inView && onSectionExp) {
      onSectionExp({
        mkt_name: '防蓝光眼镜优势',
      });
    }
  }, [inView, onSectionExp]);

  if (!advantagesData) return null;

  return (
    <div
      ref={(node) => {
        // 为埋点设置ref
        expRef(node);
        // 为动画设置ref（仅PC端）
        if (!isMobile && advantagesRef && typeof advantagesRef !== 'function') {
          (advantagesRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }}
      className={`advantages-part blue-light-advantages-part border-b border-[#3e4758] ${
        isMobile
          ? 'flex flex-col py-[34px] px-[20px]'
          : 'flex justify-center items-center py-[64px]'
      }`}
    >
      {/* 左侧图片 */}
      {advantagesData.left_image && (
        <div className={`img-wrap ${isMobile ? 'mb-[18px] px-[8px]' : 'mr-[114px]'}`}>
          <Image
            src={advantagesData.left_image || ''}
            alt={advantagesData.title}
            width={633}
            height={400}
            className={`advantages-img-wrap-img h-auto ${
              isMobile ? 'w-full' : 'w-[633px] opacity0'
            }`}
          />
        </div>
      )}

      {/* 右侧内容 */}
      <div className="right-part">
        {/* 标题 */}
        {advantagesData.title && (
          <h2
            className={`font-bs text-white advantages-right-part-h2 ${
              isMobile
                ? 'w-full mb-[16px] text-[20px] leading-[30px]'
                : 'w-[610px] mb-[40px] text-[40px] leading-[60px] opacity0'
            }`}
            style={{
              background: 'linear-gradient(90deg, #FFFFFF 0%, #A1BCFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {advantagesData.title}
          </h2>
        )}

        {/* 优势列表 */}
        {advantagesData.words && (
          <ul
            className={`grid ${
              isMobile
                ? 'grid-cols-2 gap-x-[5px] gap-y-[20px]'
                : 'grid-cols-2 gap-x-[63px] gap-y-[24px]'
            }`}
          >
            {advantagesData.words.map((item, index) => (
              <li
                key={index}
                className={`flex items-center advantages-right-part-li ${
                  isMobile ? 'text-[11px] leading-[16px]' : 'text-[14px] leading-[20px] opacity0'
                }`}
                style={{ letterSpacing: isMobile ? '0.3px' : 'normal' }}
              >
                <Image
                  src={item.image}
                  alt={item.title}
                  width={40}
                  height={40}
                  className={`${isMobile ? 'w-[32px] mr-[4px]' : 'w-[40px] mr-[13px]'}`}
                />
                <p className="text-white">{item.title}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
